/* eslint-disable react/jsx-props-no-spreading */
import { FC, lazy, Suspense } from 'react';
import { Route, RouteProps, Switch, Redirect } from 'react-router-dom';
import UnsupportedBrowser from 'packages/lib/common-ui/error-pages/unsupported-browser';
import PublicLayout from 'packages/main/components/Layouts/PublicLayout';
import { LoginCallback } from '@okta/okta-react';
import Contractor from 'packages/main/routes/contractor';
import MobisRoutes from 'packages/main/routes/mobis';
import { PrivateRoute } from 'packages/main/routes/components/private-route';
import { SentryRoute } from 'packages/main/routes/components/sentry-route';
import { AnnotationToolRoute } from 'packages/main/routes/components/annotation-tool-route';
import AnnotationToolSkeleton from 'packages/at-shell/components/app-shell/app-layout';
import AppPageSkeleton from 'packages/lib/common-ui/app-shell/app-layout';
import { useRecoilValue } from 'recoil';
import { uiThemeAtom } from 'packages/at-shell/state/recoil/ui';
import { PolarisLayout } from 'packages/main/pages/polaris/layout';
import polarisPortalLayoutConfig from 'packages/polaris/portal';
import polarisToolLayoutConfig from 'packages/polaris/tool';
import { LegacyTldRedirect } from 'packages/main/routes/components/legacy-tld-redirect';
// May-20-2026 Used Cursor (Auto / Claude Opus 4.7 / Sonnet 4.6) to fork code for single-task SE

const Landing = lazy(() => import('packages/main/pages/home-page'));
const DataManagement = lazy(() => import('packages/main/pages/data-man'));

/**
 * ANCHOR – Error Pages
 */
const ErrorPage404 = lazy(
  () => import('packages/lib/common-ui/error-pages/error-page-404')
);
const ErrorPage403 = lazy(
  () => import('packages/lib/common-ui/error-pages/error-page-403')
);
const LidarDataView = lazy(
  () => import('packages/main/components/lidar-data-view/lidar-data-view')
);

/**
 * ANCHOR – Legacy Data Management Pages
 */
const SampleView = lazy(
  () => import('packages/main/components/sample-view/sample-view')
);
const SensorView = lazy(
  () => import('packages/main/components/sensor-view/sensor-view')
);
const SampleGroups = lazy(
  () =>
    import('packages/main/components/sample-groups/sample-groups-search-table')
);
const SampleGroup = lazy(
  () => import('packages/main/components/sample-groups/sample-group')
);
const TaskGroup = lazy(
  () => import('packages/main/components/TaskGroup/TaskGroup')
);
const Buckets = lazy(() => import('packages/main/components/Buckets/Buckets'));
const Download = lazy(() => import('packages/main/components/Download'));
const Taxonomy = lazy(
  () => import('packages/main/components/Taxonomy/Taxonomy')
);
const Attributes = lazy(
  () => import('packages/main/components/Management/Attributes')
);
const Instructions = lazy(
  () => import('packages/main/components/Management/Instructions')
);
const Categories = lazy(
  () => import('packages/main/components/Management/Categories')
);
const InstructionDetail = lazy(
  () => import('packages/main/components/Management/InstructionDetail')
);
const AttributeDetail = lazy(
  () => import('packages/main/components/Management/AttributeDetail')
);
const CategoryDetail = lazy(
  () => import('packages/main/components/Management/CategoryDetail')
);
const TaskDetail = lazy(
  () => import('packages/main/components/TaskGroup/TaskDetail')
);
const Images = lazy(() => import('packages/instance-seg/image-tool'));
const TaskGroupsBaseType = lazy(
  () =>
    import(
      'packages/main/components/TaskGroups/TaskGroupType/task-groups-base-type'
    )
);
const Videos = lazy(() => import('packages/main/components/Videos/landing'));
const TaskLookup = lazy(
  () => import('packages/main/pages/annotation-task-portal/task-lookup')
);
const TLDTool = lazy(() => import('packages/main/pages/tld-tool'));

const Pipelines = lazy(
  () => import('packages/main/components/pipelines/pipelines')
);
const Workflow = lazy(
  () => import('packages/main/components/workflow/workflow')
);
const TldHome = lazy(
  () => import('packages/main/components/tld-home/tld-home')
);
const TrainingJobs = lazy(
  () =>
    import(
      'packages/main/components/training-platform/training-platform-landing'
    )
);
const JobMetrics = lazy(
  () => import('packages/main/components/job-metrics/job-metrics-landing')
);
const AudioTool = lazy(
  () => import('packages/main/components/audio-tool/audio-tool-landing')
);
const DTBTool = lazy(
  () => import('packages/main/components/dtb-tool/dtb-landing')
);

/**
 * ANCHOR – Auth Pages
 */
const SignOut = lazy(
  () => import('packages/main/routes/components/signout-route')
);

/**
 * ANCHOR - Annotation Task Portal Pages
 */
const AnnotationTaskPortal = lazy(
  () => import('packages/main/pages/annotation-task-portal/tools')
);
const TaskPipelines = lazy(
  () => import('packages/main/pages/annotation-task-portal/pipelines')
);
const SampleGroupQueues = lazy(
  () => import('packages/main/pages/annotation-task-portal/sample-group-queue')
);

const MultiviewScene = lazy(
  () => import('packages/main/pages/multiview-scene')
);

/**
 * ANCHOR - Annotation Tool Pages
 */
const SemSegTool = lazy(() => import('packages/main/pages/sem-seg'));
const InstanceSegTool = lazy(
  () => import('packages/main/pages/instance-seg-poc')
);
const ClassificationEnrichmentTool = lazy(
  () => import('packages/main/pages/classification-enrichment')
);
const SceneEnrichmentTool = lazy(
  () => import('packages/main/pages/scene-enrichment')
);
const SceneEnrichmentToolLegacy = lazy(
  () => import('packages/main/pages/scene-enrichment-legacy')
);
const InstanceSegToolLegacy = lazy(
  () => import('packages/main/pages/instance-seg')
);
const EVATool = lazy(() => import('packages/main/pages/eva-tool'));
const LidarBox = lazy(() => import('packages/main/pages/lidar-box'));
const LidarBoxEnrichment = lazy(
  () => import('packages/main/pages/lidar-box-enrichment')
);
const LidarBoxAmplification = lazy(
  () => import('packages/main/pages/lidar-box-amplification')
);
const LidarTopDown = lazy(() => import('packages/main/pages/lidar-top-down'));
const LidarSeg = lazy(() => import('packages/main/pages/lidar-seg'));
const RosterManagement = lazy(() => import('packages/main/pages/roster-man'));

/**
 * ANCHOR - Non-annotation tools updated to use at-shell
 */

const DataDiscovery = lazy(() => import('packages/main/pages/data-discovery'));
const Omnitag = lazy(() => import('packages/main/pages/omnitag'));
const RequestMan = lazy(() => import('packages/main/pages/request-man'));
const DataMiningQA = lazy(() => import('packages/main/pages/data-mining-qa'));

const PublicRoute: FC<React.PropsWithChildren<RouteProps>> = ({
  component: Component,
  children,
  ...rest
}) => (
  <SentryRoute
    {...rest}
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    render={(props: any): JSX.Element => (
      <PublicLayout>
        {Component ? <Component {...props} /> : children}
      </PublicLayout>
    )}
  />
);

const PolarisTool = lazy(() => import('packages/main/pages/polaris/search'));
const PolarisTools = lazy(() => import('packages/main/pages/polaris/tools'));
const PolarisDataRequests = lazy(
  () => import('packages/main/pages/polaris/data-requests')
);

export const Routes: FC<React.PropsWithChildren<unknown>> = () => {
  const theme = useRecoilValue(uiThemeAtom);

  return (
    <div className="full-height">
      <Suspense fallback={null}>
        <Switch>
          <PrivateRoute path="/" exact component={Landing} />

          <AnnotationToolRoute path="/polaris/tools" exact>
            <PolarisLayout appLayout={polarisPortalLayoutConfig}>
              <PolarisTools />
            </PolarisLayout>
          </AnnotationToolRoute>
          <AnnotationToolRoute path="/polaris/data-requests" exact>
            <PolarisLayout appLayout={polarisPortalLayoutConfig}>
              <PolarisDataRequests />
            </PolarisLayout>
          </AnnotationToolRoute>
          <AnnotationToolRoute path="/polaris/:query?" exact>
            <PolarisLayout appLayout={polarisToolLayoutConfig}>
              <PolarisTool />
            </PolarisLayout>
          </AnnotationToolRoute>
          <PrivateRoute exact path="/pipelines" component={Pipelines} />
          <PrivateRoute
            exact
            path="/workflow/:workflowToken"
            component={Workflow}
          />
          <PrivateRoute exact path="/task-groups/:pipeline?">
            <TaskGroupsBaseType />
          </PrivateRoute>
          <PrivateRoute
            path={[
              '/task-group/:type?/:taskGroupToken',
              '/task-groups/:type?/:taskGroupToken',
            ]}
            component={TaskGroup}
          />
          <PrivateRoute path="/task/:token?" component={TaskDetail} />
          <PrivateRoute path="/images/:token?" component={Images} />
          <PrivateRoute
            path="/sample-groups/:workflowToken/:sampleGroupToken"
            component={SampleGroup}
          />
          <PrivateRoute
            path="/sample-group/:sampleGroupToken"
            component={SampleGroup}
          />
          <PrivateRoute path="/sample-groups/" component={SampleGroups} />
          {/* We can use * for 'zero or more matches' for the key, to match multiple directories */}
          <PrivateRoute path="/dbs/:bucket/:key*" component={Buckets} />
          <PrivateRoute path="/dbu/:bucket/:key*" component={Download} />
          <PrivateRoute
            path="/taxonomy/:categoryPath?/:activePage?"
            component={Taxonomy}
          />
          <PrivateRoute path="/categories/:token" component={CategoryDetail} />
          <PrivateRoute path="/categories" component={Categories} />
          <PrivateRoute path="/attributes/:token" component={AttributeDetail} />
          <PrivateRoute path="/attributes" component={Attributes} />
          <PrivateRoute
            path="/instructions/:token"
            component={InstructionDetail}
          />
          <PrivateRoute path="/instructions" component={Instructions} />
          <PrivateRoute path="/sensor/:token?" component={SensorView} />
          <PrivateRoute path="/sample/:token?" component={SampleView} />
          <PrivateRoute path="/lidar-data/:token?" component={LidarDataView} />
          <PrivateRoute path="/scene-hunting" component={Videos} />
          <PrivateRoute path="/task-lookup" component={TaskLookup} />
          <PrivateRoute path="/videos" component={Videos} />
          <PrivateRoute path="/tld-home" component={TldHome} />
          <PrivateRoute path="/training-jobs" component={TrainingJobs} />
          <PrivateRoute path="/dude-wheres-my-log" component={JobMetrics} />
          <PrivateRoute path="/audio-tool/:mode?" component={AudioTool} />
          <PrivateRoute path="/dtb/:dtbid?" component={DTBTool} />
          <AnnotationToolRoute path="/roster-management">
            <Suspense fallback={<AppPageSkeleton />}>
              <RosterManagement />
            </Suspense>
          </AnnotationToolRoute>
          {/**
           *
           * ANCHOR – Contractor Routes
           *
           */}
          {/* this one is different because it contains a whole family of sub-urls */}
          <Route path="/contractor">
            <Contractor />
          </Route>
          {/**
           *
           * ANCHOR – Contractor Routes
           *
           */}
          {/* this one is different because it contains a whole family of sub-urls */}
          <Route path="/mobis">
            <MobisRoutes />
          </Route>
          {/**
           *
           * ANCHOR – Data Management
           *
           */}
          <AnnotationToolRoute path="/data-management" exact>
            <Suspense fallback={<AppPageSkeleton />}>
              <DataManagement />
            </Suspense>
          </AnnotationToolRoute>

          {/**
           *
           * ANCHOR – Annotation Task Portal
           *
           */}
          <AnnotationToolRoute path="/tools/multiview-scene">
            <MultiviewScene />
          </AnnotationToolRoute>
          <AnnotationToolRoute path="/tools" exact>
            <Suspense fallback={<AppPageSkeleton />}>
              <AnnotationTaskPortal />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute path="/tasks" exact>
            <Suspense fallback={<AppPageSkeleton />}>
              <AnnotationTaskPortal />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute path="/tasks/:workflow" exact>
            <Suspense fallback={<AppPageSkeleton />}>
              <TaskPipelines />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path="/tasks/:workflow/:pipeline/:task_state"
            exact
          >
            <Suspense fallback={<AppPageSkeleton />}>
              <SampleGroupQueues />
            </Suspense>
          </AnnotationToolRoute>
          {/**
           *
           * ANCHOR – Annotation Tools
           *
           */}
          <AnnotationToolRoute
            path="/sem-seg/:extraction_token/:task_token?"
            exact
          >
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <SemSegTool />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute path="/eva/:extraction_token/:task_token?" exact>
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <EVATool />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path="/instance-seg/:extraction_token/:task_token?"
            exact
          >
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <InstanceSegTool />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path="/instance-seg-legacy/:extraction_token/:task_token?"
            exact
          >
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <InstanceSegToolLegacy />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute path="/classification-enrichment" exact>
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <ClassificationEnrichmentTool />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path="/scene-enrichment/:extraction_token/:task_token?"
            exact
          >
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <SceneEnrichmentTool />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute path="/scene-enrichment-legacy" exact>
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <SceneEnrichmentToolLegacy />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path="/lidar-box/:extraction_token/:task_token?"
            exact
          >
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme={theme} />}
            >
              <LidarBox />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path="/lidar-box-enrichment/:extraction_token/:task_token?"
            exact
          >
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme={theme} />}
            >
              <LidarBoxEnrichment />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path="/lidar-box-amplification/:extraction_token/:task_token?"
            exact
          >
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme={theme} />}
            >
              <LidarBoxAmplification />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path="/lidar-top-down/:extraction_token/:task_token?"
            exact
          >
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme={theme} />}
            >
              <LidarTopDown />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path="/lidar-seg/:extraction_token/:task_token?"
            exact
          >
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme={theme} />}
            >
              <LidarSeg />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path="/traffic-light-detection/:extraction_token/:task_token?"
            exact
          >
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <TLDTool />
            </Suspense>
          </AnnotationToolRoute>
          {/* Support legacy links created before TLD tool was integrated with Task Management */}
          <AnnotationToolRoute
            path="/tld-video/:extraction_token/:readonly?"
            exact
          >
            <LegacyTldRedirect />
          </AnnotationToolRoute>
          {/**
           *
           * ANCHOR – Non-Annotation Tools that use At-Shell
           *
           */}
          <AnnotationToolRoute path="/data-discovery/:query?" exact>
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme={theme} />}
            >
              <DataDiscovery />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute path="/omnitag/:query?" exact>
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme={theme} />}
            >
              <Omnitag />
            </Suspense>
          </AnnotationToolRoute>

          <AnnotationToolRoute path="/data-requests" exact>
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme={theme} />}
            >
              <RequestMan />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute path="/data-mining-qa" exact>
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme={theme} />}
            >
              <DataMiningQA />
            </Suspense>
          </AnnotationToolRoute>
          {/**
           *
           * ANCHOR – Authentication Routes
           *
           */}
          <SentryRoute path="/callback" component={LoginCallback} />
          <SentryRoute path="/contractor-callback" component={LoginCallback} />
          <SentryRoute path="/mobis-callback" component={LoginCallback} />
          <PublicRoute
            path={[
              '/signout',
              '/sign-out',
              '/logoff',
              '/log-off',
              '/logout',
              '/log-out',
              '/contractor/signout',
              '/contractor/sign-out',
              '/contractor/logoff',
              '/contractor/log-off',
              '/contractor/logout',
              '/contractor/log-out',
              '/mobis/signout',
              '/mobis/sign-out',
              '/mobis/logoff',
              '/mobis/log-off',
              '/mobis/logout',
              '/mobis/log-out',
            ]}
            component={SignOut}
          />
          {/**
           * ANCHOR - Handle awkwardly non-existent paths
           */}
          <AnnotationToolRoute path="/tasks" exact>
            <Redirect to="/tools" />
          </AnnotationToolRoute>
          <AnnotationToolRoute path="/tasks/:workflow/:no_path" exact>
            <Redirect to="/tools" />
          </AnnotationToolRoute>
          <AnnotationToolRoute exact path="/tasks/sem-seg/:no_path">
            <Redirect to="/tasks/sem-seg" />
          </AnnotationToolRoute>
          <AnnotationToolRoute exact path="/tasks/lidar-box/:no_path">
            <Redirect to="/tasks/lidar-box" />
          </AnnotationToolRoute>
          <AnnotationToolRoute
            exact
            path="/tasks/lidar-box-enrichment/:no_path"
          >
            <Redirect to="/tasks/lidar-box-enrichment" />
          </AnnotationToolRoute>
          <AnnotationToolRoute
            exact
            path="/tasks/lidar-box-amplification/:no_path"
          >
            <Redirect to="/tasks/lidar-box-amplification" />
          </AnnotationToolRoute>
          <AnnotationToolRoute exact path="/tasks/lidar-top-down/:no_path">
            <Redirect to="/tasks/lidar-top-down" />
          </AnnotationToolRoute>
          <AnnotationToolRoute exact path="/tasks/lidar-seg/:no_path">
            <Redirect to="/tasks/lidar-seg" />
          </AnnotationToolRoute>
          <AnnotationToolRoute exact path="/tasks/eva/:no_path">
            <Redirect to="/tasks/eva" />
          </AnnotationToolRoute>
          <AnnotationToolRoute exact path="/tasks/audio-labeling/:no_path">
            <Redirect to="/tasks/audio-labeling" />
          </AnnotationToolRoute>
          {/**
           *
           * ANCHOR – Route Errors
           *
           */}
          <AnnotationToolRoute path="/403" exact>
            <ErrorPage403 />
          </AnnotationToolRoute>
          {/* <PublicRoute path="/403" component={ErrorPage403} />{' '} */}
          <PublicRoute
            path="/unsupported-browser"
            component={UnsupportedBrowser}
          />
          <AnnotationToolRoute path="/:no_path" exact>
            <ErrorPage404 />
          </AnnotationToolRoute>
          <AnnotationToolRoute path="/:no_path/*" exact>
            <ErrorPage404 />
          </AnnotationToolRoute>
          <Route path="*" component={ErrorPage404} />
        </Switch>
      </Suspense>
    </div>
  );
};
export default Routes;
